import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { AlertCircle, ArrowRight, Check, History, LockKeyhole, Loader2, ShieldCheck, X, Search, HelpCircle } from "lucide-react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
  initializeForecastRun,
  useGetForecastOutlook,
  useUpdateForecastDecision,
  useCompleteForecastRun,
  useGetForecastAudit,
  getGetForecastOutlookQueryKey,
  getGetForecastAuditQueryKey,
} from "@workspace/api-client-react";
import type {
  ForecastOutlook,
  ForecastDecision,
  ForecastRollingPeriod,
  ForecastRunInputExplainHandoffItem,
  ForecastDecisionTreatment,
  ForecastDecisionMethod,
  ForecastAvailability,
  ForecastDecisionUpdateStatus,
} from "@workspace/api-client-react";
import { Shell, Tag, PageHead, formatMoney } from "@/components/tekledger-ui";
import { ForecastInputs, MonthlyAvailabilityValue } from "@/components/forecast-inputs/index";
import { useWorkspaceCompany } from "@/hooks/use-workspace-company";

const TREATMENT_LABELS: Record<string, string> = {
  no_future_impact: "No Future Impact",
  timing_shift: "Timing Shift",
  one_time_future_adjustment: "One-Time Future Adjustment",
  run_rate_change: "Run-Rate Change",
  remaining_fy_adjustment: "Remaining FY Adjustment",
};

const METHOD_LABELS: Record<string, string> = {
  remaining_budget: "Remaining Budget",
  run_rate: "Run Rate",
  commitment_schedule: "Commitment / Contract Schedule",
  planned_driver: "Planned / Driver-Based",
  insufficient_evidence: "Insufficient Evidence",
  mixed: "Mixed Methods",
};

const METHOD_ORDER = ["run_rate", "remaining_budget", "commitment_schedule", "planned_driver", "insufficient_evidence"] as const;

function phasingForMethod(method: string) {
  if (method === "commitment_schedule") return { method: "Commitment Schedule", basis: "Authoritative obligation dates and monthly amounts." };
  if (method === "planned_driver") return { method: "Approved Plan Schedule", basis: "Approved plan or driver monthly schedule." };
  if (method === "remaining_budget") return { method: "Published Budget Monthly Values", basis: "The authoritative Budget values for each open period are used without re-spreading." };
  if (method === "run_rate") return { method: "Monthly Run Rate", basis: "The eligible certified-history average is applied to each open period." };
  if (method === "mixed") return { method: "Mixed Source Schedules", basis: "Each open period follows its strongest available source." };
  return { method: "Not Available", basis: "No supported amount exists to phase across future periods." };
}

const STATUS_LABELS: Record<string, string> = {
  needs_decision: "Needs Decision",
  accepted: "Accepted",
  adjusted: "Adjusted",
  no_future_impact: "No Future Impact",
  needs_evidence: "Needs Evidence",
};

const pendingForecastInitializations = new Map<string, Promise<ForecastOutlook>>();
const forecastInitializationAttempts = new Map<string, Error | null>();
const FORECAST_INITIALIZATION_FRESHNESS_MS = 30_000;
const FORECAST_MINIMUM_REVEAL_MS = 5_200;

function initializeForecastOnce(
  contextKey: string,
  input: Parameters<typeof initializeForecastRun>[0],
) {
  const pending = pendingForecastInitializations.get(contextKey);
  if (pending) return pending;

  forecastInitializationAttempts.set(contextKey, null);
  const request = initializeForecastRun(input);
  pendingForecastInitializations.set(contextKey, request);
  void request.then(
    () => {
      window.setTimeout(() => {
        if (pendingForecastInitializations.get(contextKey) === request) {
          pendingForecastInitializations.delete(contextKey);
          forecastInitializationAttempts.delete(contextKey);
        }
      }, FORECAST_INITIALIZATION_FRESHNESS_MS);
    },
    (cause: unknown) => {
      if (pendingForecastInitializations.get(contextKey) === request) {
        pendingForecastInitializations.delete(contextKey);
        forecastInitializationAttempts.set(
          contextKey,
          cause instanceof Error ? cause : new Error("Forecast initialization failed."),
        );
      }
    },
  );
  return request;
}

function formatPeriodName(period: number, fy: number) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[period - 1]} ${fy}`;
}

function AvailabilityValue({ value }: { value: ForecastAvailability }) {
  if (value.state === "no_source_data") return <span className="mini">No source data</span>;
  if (value.state === "no_records") return <span className="mini">No records</span>;
  if (value.state === "verified_zero" || value.amount === 0) return <span>$0</span>;
  return <span>{formatMoney(value.amount)}</span>;
}

export default function ForecastPage() {
  const { companyId } = useWorkspaceCompany();
  const search = useSearch();
  const [, setLocation] = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const runId = params.get("run");
  const fy = Number(params.get("fy") || "2026");
  const period = Number(params.get("period") || "7");
  const departmentCode = params.get("department") || "643";
  const explanationsRaw = params.get("explanations");
  const returnToRaw = params.get("returnTo");
  const returnToExplain = returnToRaw?.startsWith("/explain?") ? returnToRaw : null;
  const initialView = params.get("view") === "rolling" ? "rolling" : "fy";

  const queryClient = useQueryClient();
  const initializationAttempted = useRef(false);
  const initializationInFlight = useRef(false);
  const initializationAttemptId = useRef(0);
  const forecastScreenMounted = useRef(false);
  const activeInitialView = useRef(initialView);
  activeInitialView.current = initialView;
  const [initializationError, setInitializationError] = useState<Error | null>(null);
  const [isRetryingInitialization, setIsRetryingInitialization] = useState(false);
  const [initializationSeconds, setInitializationSeconds] = useState(0);
  const initializationStartedAt = useRef(Date.now());
  const explainHandoff = useMemo<ForecastRunInputExplainHandoffItem[]>(() => {
    if (!explanationsRaw) return [];
    try {
      const parsed = JSON.parse(explanationsRaw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((record) => {
        const reversalExpectation = record.expected_to_reverse === true ? "Yes"
          : record.expected_to_reverse === false ? "No"
          : ["Yes", "No", "Partially", "Unknown"].includes(record.expected_to_reverse) ? record.expected_to_reverse
          : "Unknown";
        return {
          glAccount: String(record.variance_id || record.glAccount || ""),
          reason: String(record.reason || "Unexplained"),
          nature: String(record.nature || "Unknown"),
          expectedToReverse: reversalExpectation === "Yes",
          reversalExpectation,
          reversalPeriod: record.expected_reversal_period || record.reversalPeriod || null,
          owner: String(record.owner || ""),
          narrative: String(record.narrative || ""),
          status: String(record.status || "Unexplained"),
        };
      }).filter((record) => record.glAccount);
    } catch {
      return [];
    }
  }, [explanationsRaw]);
  const initializationContextKey = useMemo(
    () => JSON.stringify([companyId, fy, period, departmentCode, explainHandoff]),
    [companyId, departmentCode, explainHandoff, fy, period],
  );
  const activeInitializationContext = useRef(initializationContextKey);

  useEffect(() => {
    forecastScreenMounted.current = true;
    if (activeInitializationContext.current !== initializationContextKey) {
      activeInitializationContext.current = initializationContextKey;
      initializationAttempted.current = false;
      initializationInFlight.current = false;
      initializationAttemptId.current += 1;
      setInitializationError(null);
      setIsRetryingInitialization(false);
    }

    return () => {
      forecastScreenMounted.current = false;
    };
  }, [initializationContextKey]);

  const runForecast = useCallback((isRetry = false) => {
    if (initializationInFlight.current) return;
    initializationInFlight.current = true;
    initializationStartedAt.current = Date.now();
    const attemptId = ++initializationAttemptId.current;
    const attemptContext = initializationContextKey;
    const ownsAttempt = () => forecastScreenMounted.current
      && attemptId === initializationAttemptId.current
      && attemptContext === activeInitializationContext.current;
    setInitializationError(null);
    if (isRetry) setIsRetryingInitialization(true);
    initializeForecastOnce(initializationContextKey, {
      companyId,
      fiscalYear: fy,
      throughPeriod: period,
      departmentCode,
      actor: "ER",
      explainHandoff,
    }).then((data) => {
      if (!ownsAttempt()) return;
      initializationInFlight.current = false;
      setIsRetryingInitialization(false);
      queryClient.setQueryData(getGetForecastOutlookQueryKey(data.runId), data);
      const next = new URLSearchParams({
        run: data.runId,
        fy: String(fy),
        period: String(period),
        department: departmentCode,
        view: activeInitialView.current,
      });
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const remainingRevealMs = reducedMotion
        ? 900
        : Math.max(0, FORECAST_MINIMUM_REVEAL_MS - (Date.now() - initializationStartedAt.current));
      window.setTimeout(() => {
        if (!ownsAttempt()) return;
        setLocation(`/forecast?${next.toString()}`, { replace: true });
      }, remainingRevealMs);
    }).catch((cause: unknown) => {
      if (!ownsAttempt()) return;
      initializationInFlight.current = false;
      setIsRetryingInitialization(false);
      setInitializationError(cause instanceof Error ? cause : new Error("Forecast initialization failed."));
    });
  }, [departmentCode, explainHandoff, fy, initializationContextKey, period, queryClient, setLocation]);

  useEffect(() => {
    if (!runId && explanationsRaw !== null && !initializationAttempted.current) {
      initializationAttempted.current = true;
      const retainedError = forecastInitializationAttempts.get(initializationContextKey);
      if (retainedError) {
        setInitializationError(retainedError);
        return;
      }
      runForecast();
    }
  }, [explanationsRaw, initializationContextKey, runForecast, runId]);

  useEffect(() => {
    if (runId || explanationsRaw === null) return;
    initializationStartedAt.current = Date.now();
    const timer = window.setInterval(() => {
      setInitializationSeconds(Math.floor((Date.now() - initializationStartedAt.current) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [runId, explanationsRaw]);

  const backToExplain = () => {
    if (returnToExplain) {
      setLocation(returnToExplain);
      return;
    }
    window.history.back();
  };

  if (!runId && !explanationsRaw) {
    return (
      <Shell active="Forecast">
        <section className="tl-content">
          <PageHead eyebrow="Forecast" title="Working Forecast" sub="Deterministic, documented estimates." />
          <div className="card" style={{ textAlign: "center", padding: "60px 20px" }}>
            <AlertCircle size={32} style={{ margin: "0 auto 16px", color: "#8d4b39" }} />
            <h3>No Explain handoff found</h3>
            <p className="sub" style={{ margin: "8px auto 20px" }}>A forecast run must be initialized from the Explain phase to ensure variances are accounted for.</p>
            <button className="tl-btn primary" onClick={() => setLocation("/variance")}>Return to Variance</button>
          </div>
        </section>
      </Shell>
    );
  }

  if (!runId) {
    const initializationStage = initializationSeconds < 1
      ? 0
      : initializationSeconds < 2
        ? 1
        : initializationSeconds < 4
          ? 2
          : 3;
    const initializationStages = [
      { label: "Reading the Explain handoff", detail: `${explainHandoff.length} source-backed variance records carried forward` },
      { label: "Locking the evidence base", detail: "Checking certified Actuals and the Published Budget before calculation" },
      { label: "Applying the forecast logic", detail: "Selecting evidence-weighted methods, timing, and future-period treatments" },
      { label: "Balancing the outlook", detail: "Reconciling monthly values before opening your Forecast workbench" },
    ];
    const lastKnownState = initializationStages[initializationStage].label;
    const showInitializationError = initializationError !== null || isRetryingInitialization;
    return (
      <Shell active="Forecast">
        <section className="tl-content">
          <div className="explain-header" style={{ marginBottom: 24 }}>
            <div className="forecast-header-row">
              <div>
                <div className="eyebrow" style={{ marginBottom: 6 }}>FORECAST</div>
                <h1 style={{ margin: "0 0 4px", fontSize: 24 }}>FY{fy} | Through {formatPeriodName(period, fy)} | {departmentCode} Corporate Systems</h1>
              </div>
              <div className="vh-workflow" style={{ margin: 0 }}>
                <div className="wf-step active"><Check size={11} style={{ color: "#2d7667" }} /> Variance</div>
                <div className="wf-step active"><Check size={11} style={{ color: "#2d7667" }} /> Explain</div>
                <div className="wf-step active"><span className="wf-dot" style={{ background: "currentColor" }} /> Forecast</div>
                <div className="wf-step"><span className="wf-dot empty" /> Review</div>
              </div>
            </div>
          </div>
          <div className="forecast-handoff-context">
            <div><span>Explain handoff</span><b>{explainHandoff.length} material variances dispositioned</b></div>
            <div><span>Calculation</span><b>Evidence → method → reconciled outlook</b></div>
          </div>
          <div className="forecast-initialization-card card">
            <div className="forecast-initialization-status" role="status" aria-live="polite" data-testid="forecast-initialization-status">
            <div className="forecast-initialization-kicker"><span className="forecast-pulse" /> {showInitializationError ? "FORECAST INITIALIZATION" : "CALCULATING FORECAST"}</div>
            <h3>{isRetryingInitialization ? "Trying Forecast preparation again" : showInitializationError ? "Forecast could not be prepared" : initializationStages[initializationStage].label}</h3>
            {!showInitializationError && <p className="forecast-initialization-message">{initializationStages[initializationStage].detail}. Stay with it—TEKLEDGER is turning the handoff into a defensible outlook.</p>}
            </div>
            {showInitializationError && (
              <div className="source-page-error" role="alert" style={{ marginBottom: 16 }}>
                <AlertCircle size={14} />
                <div><b>The Explain handoff is still preserved.</b><span>Authoritative Forecast sources could not be resolved.</span></div>
              </div>
            )}
            {!showInitializationError && (
              <div className="forecast-initialization-steps">
                {initializationStages.map((stage, index) => <div className={`forecast-initialization-step ${index === initializationStage ? "active" : ""} ${index < initializationStage ? "past" : ""}`} key={stage.label}>
                  <span className="forecast-step-mark">{index < initializationStage ? <Check size={11} /> : index === initializationStage ? <span className="forecast-step-dot" /> : index + 1}</span>
                  <div><b>{stage.label}</b><span>{stage.detail}</span></div>
                </div>)}
              </div>
            )}
            {!showInitializationError && (
              <div className="forecast-calculation-trace" data-testid="forecast-calculation-trace" aria-label="Live forecast calculation trace">
                <div className="forecast-trace-head">
                  <span><span className="forecast-trace-signal" /> Calculation trace</span>
                  <b>LIVE MODELING</b>
                </div>
                <div className="forecast-trace-equation" aria-label="Forecast calculation equation">
                  <span>Certified Actuals</span>
                  <i>+</i>
                  <span>Explain decisions</span>
                  <i>+</i>
                  <span>Budget evidence</span>
                  <i>→</i>
                  <strong>Forecast Outlook</strong>
                </div>
                <div className="forecast-trace-signals">
                  <span><b>{explainHandoff.length}</b> decisions carried forward</span>
                  <span><b>12</b> periods checked</span>
                  <span><b>1</b> reconciled view</span>
                </div>
              </div>
            )}
            {!showInitializationError && <div className="forecast-initialization-footer"><span className="forecast-progress-bar"><i /></span><span>{initializationSeconds < 3 ? "This usually takes a few seconds." : initializationSeconds < 8 ? "Still preparing the current outlook. Larger forecast populations can take a few seconds." : "Still working. The Explain handoff is preserved while TEKLEDGER completes Forecast initialization."}</span><b>{initializationSeconds}s</b></div>}
            {showInitializationError && <div className="forecast-initialization-error-context"><span>Last known state</span><b>{lastKnownState}</b>{initializationError && <details><summary>View details</summary><p>{initializationError.message}</p></details>}</div>}
            {showInitializationError && <div className="forecast-initialization-actions"><button className="tl-btn primary" disabled={isRetryingInitialization} aria-busy={isRetryingInitialization} onClick={() => { initializationAttempted.current = true; initializationStartedAt.current = Date.now(); setInitializationSeconds(0); runForecast(true); }}>{isRetryingInitialization ? "Trying Again…" : "Try Again"}</button><button className="tl-btn" onClick={backToExplain}>Back to Explain</button></div>}
          </div>
        </section>
      </Shell>
    );
  }

  return <ActiveForecastRun runId={runId} initialView={initialView} />;
}

function ActiveForecastRun({ runId, initialView }: { runId: string; initialView: "fy"|"rolling" }) {
  const [, setLocation] = useLocation();
  const [workbench, setWorkbench] = useState<"fy" | "rolling">(initialView);
  const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(null);
  const [outlookOverride, setOutlookOverride] = useState<ForecastOutlook | null>(null);
  
  const [drawer, setDrawer] = useState<"audit" | "methods" | "basis" | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterMethod, setFilterMethod] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"status"|"account">("status");
  const [methodDetailDecisionId, setMethodDetailDecisionId] = useState<string | null>(null);
  const initialDecisionSelected = useRef(false);
  const suppressInitialDecisionScroll = useRef(true);

  const queryClient = useQueryClient();
  const pendingUpdates = useIsMutating({ mutationKey: ["updateForecastDecision"] });
  const completeMutation = useCompleteForecastRun();
  
  const { data: outlookBase, isLoading: isOutlookLoading, isError: isOutlookError } = useGetForecastOutlook(runId, {
    query: { queryKey: getGetForecastOutlookQueryKey(runId), staleTime: 30_000 },
  });
  const outlook = outlookOverride ?? outlookBase;
  const { data: audit, isLoading: isAuditLoading } = useGetForecastAudit(runId);
  
  const filteredDecisions = useMemo(() => {
    if (!outlook) return [];
    let list = outlook.decisions.filter(d => {
      if (filterStatus !== "all" && d.status !== filterStatus) return false;
      if (filterMethod === "manual_override" && d.manualAdjustment === 0) return false;
      if (filterMethod !== "all" && filterMethod !== "manual_override" && d.method !== filterMethod) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!d.glAccount.toLowerCase().includes(q) && !d.explanation.reason?.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });

    if (sortBy === "status") {
      const statusWeight = {
        needs_decision: 0,
        needs_evidence: 1,
        adjusted: 2,
        accepted: 3,
        no_future_impact: 4
      };
      list.sort((a, b) => {
        const wA = statusWeight[a.status as keyof typeof statusWeight] ?? 99;
        const wB = statusWeight[b.status as keyof typeof statusWeight] ?? 99;
        if (wA !== wB) return wA - wB;
        return a.glAccount.localeCompare(b.glAccount);
      });
    } else {
      list.sort((a, b) => a.glAccount.localeCompare(b.glAccount));
    }
    return list;
  }, [outlook?.decisions, filterStatus, filterMethod, searchQuery, sortBy]);

  const methodMix = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const decision of outlook?.decisions ?? []) counts[decision.method] = (counts[decision.method] ?? 0) + 1;
    counts.manual_override = (outlook?.decisions ?? []).filter((decision) => decision.manualAdjustment !== 0).length;
    return counts;
  }, [outlook?.decisions]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if (!selectedDecisionId) return;
      
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
         e.preventDefault();
         const idx = filteredDecisions.findIndex(d => d.decisionId === selectedDecisionId);
         if (e.key === "ArrowDown" && idx >= 0 && idx < filteredDecisions.length - 1) {
           setSelectedDecisionId(filteredDecisions[idx + 1].decisionId);
         } else if (e.key === "ArrowUp" && idx > 0) {
           setSelectedDecisionId(filteredDecisions[idx - 1].decisionId);
         }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedDecisionId, filteredDecisions]);

  useEffect(() => {
    if (selectedDecisionId) {
      if (suppressInitialDecisionScroll.current) {
        suppressInitialDecisionScroll.current = false;
        return;
      }
      const el = document.getElementById(`row-${selectedDecisionId}`);
      if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedDecisionId]);

  useEffect(() => {
    if (selectedDecisionId && !filteredDecisions.some((decision) => decision.decisionId === selectedDecisionId)) {
      setSelectedDecisionId(filteredDecisions[0]?.decisionId ?? null);
    }
  }, [filteredDecisions, selectedDecisionId]);

  useEffect(() => {
    if (!initialDecisionSelected.current && outlook && outlook.authorityStatus !== "authoritative" && filteredDecisions.length > 0) {
      initialDecisionSelected.current = true;
      setSelectedDecisionId(filteredDecisions[0].decisionId);
    }
  }, [outlook, filteredDecisions]);
  
  if (isOutlookError) {
    return (
      <Shell active="Forecast">
        <section className="tl-content">
          <div className="forecast-workbench-error source-page-error" role="alert">
            <AlertCircle size={15} />
            <div><b>Forecast was prepared, but the workbench could not be opened.</b><span>Your run is preserved. Try loading it again or return to the Forecast route.</span></div>
            <button className="tl-btn" onClick={() => queryClient.invalidateQueries({ queryKey: getGetForecastOutlookQueryKey(runId) })}>Try Again</button>
          </div>
        </section>
      </Shell>
    );
  }

  if (isOutlookLoading || !outlook) {
    return (
      <Shell active="Forecast">
        <section className="tl-content">
          <div className="forecast-workbench-loading card" role="status" aria-live="polite">
            <div className="forecast-initialization-kicker"><span className="forecast-pulse" /> OPENING FORECAST WORKBENCH</div>
            <h3>Loading the prepared outlook</h3>
            <p>The Forecast run is ready. TEKLEDGER is bringing its decisions, source evidence, and reconciliation view into the workbench.</p>
            <span className="forecast-progress-bar"><i /></span>
          </div>
        </section>
      </Shell>
    );
  }

  const selectedDecision = outlook.decisions.find(d => d.decisionId === selectedDecisionId) || null;
  const methodDetailDecision = outlook.decisions.find(d => d.decisionId === methodDetailDecisionId) || null;
  const dominantMethod = METHOD_ORDER
    .map((method) => ({ method, count: methodMix[method] ?? 0 }))
    .sort((a, b) => b.count - a.count)[0];
  const showMethodCoverage = outlook.decisions.length >= 4 && dominantMethod.count / outlook.decisions.length >= 0.75;
  const isReconciled = Math.abs(outlook.reconciliationDifference) < 0.005;
  const canReview = outlook.reviewGate.complete && pendingUpdates === 0;
  const isAuthoritative = outlook.authorityStatus === "authoritative";

  const handleReviewHandoff = () => {
    completeMutation.mutate({ runId }, {
      onSuccess: () => {
        setLocation(`/review`);
      }
    });
  };

  return (
    <Shell active="Forecast">
      <section className="tl-content">
        <div className="explain-header" style={{ marginBottom: 24 }}>
           <div className="forecast-header-row">
            <div>
              <div className="eyebrow" style={{ marginBottom: 6 }}>FORECAST</div>
               <h1 style={{ margin: "0 0 4px", fontSize: 24 }}>FY{outlook.fiscalYear} | Through {formatPeriodName(outlook.throughPeriod, outlook.fiscalYear)} | {outlook.departmentCode} Corporate Systems</h1>
            </div>
            <div className="vh-workflow" style={{ margin: 0 }}>
              <div className="wf-step active"><Check size={11} style={{ color: "#2d7667" }} /> Variance</div>
              <div className="wf-step active"><Check size={11} style={{ color: "#2d7667" }} /> Explain</div>
              <div className="wf-step active"><span className="wf-dot" style={{ background: "currentColor" }} /> Forecast</div>
              <div className="wf-step"><span className="wf-dot empty" /> Review</div>
            </div>
          </div>
        </div>

        <div className="forecast-view-switch" role="tablist" aria-label="Forecast horizon">
           <button className={workbench === "fy" ? "active" : ""} onClick={() => setWorkbench("fy")} role="tab" aria-selected={workbench === "fy"}>FY{outlook.fiscalYear} Outlook</button>
          <button className={workbench === "rolling" ? "active" : ""} onClick={() => setWorkbench("rolling")} role="tab" aria-selected={workbench === "rolling"}>Rolling 12M</button>
        </div>

        {workbench === "fy" && (
          <div className="tl-kpis forecast-kpis">
            <div className="tl-kpi">
              <label>FY BUDGET</label>
              <strong>{formatMoney(outlook.fyBudget)}</strong>
              <span>Authoritative published baseline</span>
            </div>
            <div className="tl-kpi">
              <label>YTD EXPOSURE</label>
              <strong>{formatMoney(outlook.ytdExposure)}</strong>
              <div style={{ fontSize: 9, marginTop: 4, display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 8px" }}>
                <span style={{ color: "#74857e" }}>Actuals</span><b style={{ color: "#333", fontSize: 10 }}>{formatMoney(outlook.postedActuals)}</b>
                <span style={{ color: "#74857e" }}>Open Accruals</span><b style={{ color: "#333", fontSize: 10 }}><AvailabilityValue value={outlook.sourceAvailability.accruals} /></b>
              </div>
            </div>
            <div className="tl-kpi">
              <label>CURRENT FORECAST</label>
              <strong>{formatMoney(outlook.currentForecast)}</strong>
              <span>Modelled + Manual Adjustments</span>
            </div>
            <div className={`tl-kpi ${outlook.forecastVsBudget > 0 ? "bad" : outlook.forecastVsBudget < 0 ? "good" : ""}`}>
              <label>FORECAST VS BUDGET</label>
              <strong>{Math.abs(outlook.forecastVsBudget) < 0.005 ? "$0" : `${formatMoney(Math.abs(outlook.forecastVsBudget))} ${outlook.forecastVsBudget > 0 ? 'U' : 'F'}`}</strong>
              <span>Variance at FY end</span>
            </div>
          </div>
        )}

        {workbench === "fy" && (
          <ForecastInputs
            companyId={outlook.companyId}
            fiscalYear={outlook.fiscalYear}
            throughPeriod={outlook.throughPeriod}
            departmentCode={outlook.departmentCode}
            runId={runId}
            isAuthoritative={isAuthoritative}
            outlook={outlook}
            onOutlookRefreshed={(updated) => {
              setOutlookOverride(updated);
              queryClient.setQueryData(getGetForecastOutlookQueryKey(runId), updated);
            }}
          />
        )}

        {workbench === "fy" && (
           <div className="grid2 forecast-basis-layout">
            <div className="card">
              <div className="card-head">
                 <div><div className="eyebrow">Financial Control</div><h3>FORECAST BASIS</h3></div>
              </div>
              <div className="forecast-equation" style={{ display: "flex", flexDirection: "column", gap: 0, padding: 0, background: "transparent", border: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #edf0ea" }}>
                  <span style={{ color: "#546860" }}>Posted Actuals</span><b>{formatMoney(outlook.postedActuals)}</b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #edf0ea" }}>
                  <span style={{ color: "#546860" }}>Open Accruals</span><b><AvailabilityValue value={outlook.sourceAvailability.accruals} /></b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #edf0ea" }}>
                  <span style={{ color: "#546860" }}>Future Commitments</span><b><AvailabilityValue value={outlook.sourceAvailability.commitments} /></b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #edf0ea" }}>
                  <span style={{ color: "#546860" }}>Planned Spend</span><b><AvailabilityValue value={outlook.sourceAvailability.plans} /></b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #edf0ea" }}>
                  <span style={{ color: "#546860" }}>Modelled Residual</span><b>{formatMoney(outlook.modelledResidual)}</b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "2px solid #cbd5cf" }}>
                  <span style={{ color: "#546860" }}>Manual Adjustments</span><b>{formatMoney(outlook.manualAdjustments)}</b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0 8px" }}>
                  <span style={{ color: "#1e3a34", fontWeight: 700 }}>CURRENT FORECAST</span><b style={{ fontSize: 16 }}>{formatMoney(outlook.currentForecast)}</b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderTop: "1px dashed #d5deda" }}>
                  <span style={{ color: "#74857e" }}>Forecast vs Budget</span><b className={outlook.forecastVsBudget > 0 ? "bad-text" : outlook.forecastVsBudget < 0 ? "good-text" : ""}>{formatMoney(Math.abs(outlook.forecastVsBudget))} {outlook.forecastVsBudget > 0 ? 'U' : outlook.forecastVsBudget < 0 ? 'F' : ''}</b>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 0", color: isReconciled ? "#2b6f61" : "#a63f27" }}>
                  <span>Reconciliation Difference</span><b>{formatMoney(outlook.reconciliationDifference)}</b>
                </div>
                {!isReconciled && (
                  <div className="source-page-error" style={{ marginTop: 12 }}>
                    <AlertCircle size={14} /><div><b>FORECAST DOES NOT RECONCILE</b><span>Adjust components to clear the difference.</span></div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              <div className="card">
                <div className="card-head">
                   <div><div className="eyebrow">Composition</div><h3>FORECAST COVERAGE</h3></div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                    <span style={{ color: "#60746b" }}>Actual / Accrued</span><b>{outlook.coverage.actualAccrued}%</b>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                    <span style={{ color: "#60746b" }}>Committed</span><b>{outlook.coverage.committed !== null ? `${outlook.coverage.committed}%` : <span className="mini" style={{ color: "#74857e" }}>No source data</span>}</b>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                    <span style={{ color: "#60746b" }}>Planned</span><b>{outlook.coverage.planned !== null ? `${outlook.coverage.planned}%` : <span className="mini" style={{ color: "#74857e" }}>No source data</span>}</b>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                    <span style={{ color: "#60746b" }}>Modelled</span><b>{outlook.coverage.modelled}%</b>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {workbench === "rolling" && (
          <>
            {outlook.rollingBasis === "unavailable" ? (
              <section className="forecast-unavailable" role="status" style={{ marginBottom: 24 }}>
                <ShieldCheck size={24} />
                <div>
                  <div className="eyebrow">Controlled horizon</div>
                  <h2>Rolling 12M is not available from current authority</h2>
                  <p>No complete cross-year source schedule is present. TEKLEDGER will not invent a baseline or relabel the FY outlook as Rolling 12M.</p>
                </div>
              </section>
            ) : (
               <div className="grid2 forecast-rolling-layout">
                <div className="card" style={{ padding: 0 }}>
                  <div className="card-head" style={{ padding: "18px 18px 0" }}>
                    <div>
                      <div className="eyebrow">ROLLING 12-MONTH OUTLOOK</div>
                       <h3>{formatPeriodName(outlook.throughPeriod === 12 ? 1 : outlook.throughPeriod + 1, outlook.throughPeriod === 12 ? outlook.fiscalYear + 1 : outlook.fiscalYear)} – {formatPeriodName(outlook.throughPeriod, outlook.fiscalYear + 1)}</h3>
                    </div>
                  </div>
                  <div className="note" style={{ margin: "14px 18px", background: "#faf8f2", borderColor: "#e8dfcd", color: "#6a5c40" }}>
                     <b>FY{outlook.fiscalYear + 1} Budget has not been published.</b><br/>
                     Next-year forecasts use available commitments, planned spend, drivers, and deterministic modelling. Forecast-vs-Budget is unavailable where no Budget baseline exists.
                  </div>
                  <div className="table-wrap">
                    <table className="tl-fy-table">
                      <thead>
                        <tr>
                          <th>Period</th>
                          <th>Budget</th>
                          <th>Commitment</th>
                          <th>Planned</th>
                          <th>Modelled</th>
                          <th>Adjustment</th>
                          <th>Forecast</th>
                          <th>Forecast Basis</th>
                        </tr>
                      </thead>
                      <tbody>
                        {outlook.rolling12.map(p => (
                          <tr key={`${p.fiscalYear}-${p.period}`}>
                            <td>{formatPeriodName(p.period, p.fiscalYear)}</td>
                            <td>{p.budgetComparisonAvailable ? formatMoney(p.budget) : <span className="mini" style={{ color: "#74857e" }}>—</span>}</td>
                             <td>{outlook.sourceAvailability.commitments.state === "no_source_data" ? <span className="mini">No source data</span> : formatMoney(p.commitment)}</td>
                             <td>{outlook.sourceAvailability.plans.state === "no_source_data" ? <span className="mini">No source data</span> : formatMoney(p.planned)}</td>
                            <td>{formatMoney(p.modelled)}</td>
                            <td>{p.adjustment !== 0 ? formatMoney(p.adjustment) : "—"}</td>
                            <td><b>{p.forecastAvailable ? formatMoney(p.forecast) : <span className="mini" style={{ color: "#74857e" }}>Not Available</span>}</b></td>
                            <td className="mini">{p.methodReason || (p.budgetComparisonAvailable ? "Budget available" : "Insufficient source")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="card" style={{ height: "max-content" }}>
                  <div className="card-head">
                    <div className="eyebrow">12-MONTH COVERAGE</div>
                    <h3>{outlook.rollingCoverage.horizonMonths} months horizon</h3>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 11 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#60746b" }}>Budget-backed</span><b>{outlook.rollingCoverage.budgetBacked} months</b>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#60746b" }}>Commitment-backed</span><b>{outlook.rollingCoverage.commitmentBacked} months</b>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#60746b" }}>Planned/Driver-backed</span><b>{outlook.rollingCoverage.plannedBacked} months</b>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "#60746b" }}>Modelled</span><b>{outlook.rollingCoverage.modelled} months</b>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", color: outlook.rollingCoverage.insufficientSource > 0 ? "#9c4e36" : "" }}>
                      <span>Insufficient source</span><b>{outlook.rollingCoverage.insufficientSource} months</b>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {workbench === "fy" && (
          <div className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 24 }}>
            <div className="card-head" style={{ padding: "18px 18px 12px", marginBottom: 0 }}>
              <div>
                <div className="eyebrow">Horizon Layout</div>
                <h3>MONTHLY OUTLOOK</h3>
              </div>
            </div>
            <div className="table-wrap" style={{ margin: 0 }}>
              <table className="tl-fy-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Actual</th>
                    <th>Accrual</th>
                    <th>Commitment</th>
                    <th>Planned</th>
                    <th>Modelled</th>
                    <th>Outlook</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {outlook.monthlyOutlook.filter(m => m.status === "actual_accrued").map(m => (
                    <tr key={`${m.fiscalYear}-${m.period}`} style={{ background: "#fafbf9" }}>
                      <td>{formatPeriodName(m.period, m.fiscalYear)}</td>
                      <td>{formatMoney(m.postedActual)}</td>
                      <td><MonthlyAvailabilityValue value={m.accrual} globalState={outlook.sourceAvailability.accruals.state} /></td>
                      <td><MonthlyAvailabilityValue value={m.commitment} globalState={outlook.sourceAvailability.commitments.state} /></td>
                      <td><MonthlyAvailabilityValue value={m.planned} globalState={outlook.sourceAvailability.plans.state} /></td>
                      <td style={{ color: "#74857e" }}>—</td>
                      <td><b>{formatMoney(m.outlook)}</b></td>
                      <td><Tag tone="good">Actual / Accrued</Tag></td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={8} style={{ padding: 0 }}>
                      <div style={{ height: 2, background: "#d5deda", margin: "4px 0" }}></div>
                    </td>
                  </tr>
                  {outlook.monthlyOutlook.filter(m => m.status === "forecast").map(m => (
                    <tr key={`${m.fiscalYear}-${m.period}`}>
                      <td>{formatPeriodName(m.period, m.fiscalYear)}</td>
                      <td style={{ color: "#74857e" }}>—</td>
                      <td><MonthlyAvailabilityValue value={m.accrual} globalState={outlook.sourceAvailability.accruals.state} /></td>
                      <td><MonthlyAvailabilityValue value={m.commitment} globalState={outlook.sourceAvailability.commitments.state} /></td>
                      <td><MonthlyAvailabilityValue value={m.planned} globalState={outlook.sourceAvailability.plans.state} /></td>
                      <td>{formatMoney(m.modelled)}</td>
                      <td><b>{formatMoney(m.outlook)}</b></td>
                      <td><Tag>Forecast</Tag></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {workbench === "fy" && <div className="forecast-workbench">
          <div id="forecast-decisions" className="card" style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div className="card-head" style={{ padding: "18px 18px 12px", marginBottom: 0, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div className="eyebrow">Operating Workbench</div>
                  <h3>FORECAST DECISIONS</h3>
                </div>
                <Tag>{filteredDecisions.length} of {outlook.decisions.length} lines</Tag>
              </div>
              <div className="forecast-method-mix" aria-label="Forecast method mix">
                <span className="eyebrow">FORECAST METHOD MIX</span>
                <div>
                  {METHOD_ORDER.map((method) => <button key={method} type="button" onClick={() => setFilterMethod(method)} className={filterMethod === method ? "active" : ""}>
                    <span>{METHOD_LABELS[method]}</span><b>{methodMix[method] ?? 0}</b>
                  </button>)}
                  {(methodMix.manual_override ?? 0) > 0 && <button type="button" onClick={() => setFilterMethod("manual_override")} className={filterMethod === "manual_override" ? "active" : ""}><span>Manual Override</span><b>{methodMix.manual_override}</b></button>}
                </div>
              </div>
              {showMethodCoverage && <div className="forecast-method-coverage" role="status">
                <div><b>METHOD COVERAGE</b><span>{METHOD_LABELS[dominantMethod.method]} is used for {dominantMethod.count} of {outlook.decisions.length} forecast lines.</span></div>
                <button className="tl-btn" onClick={() => setFilterMethod(dominantMethod.method)}>Review {METHOD_LABELS[dominantMethod.method]} Lines</button>
              </div>}
              <div className="decision-filters" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div className="decision-search" style={{ flex: 1, display: "flex", alignItems: "center", background: "#fff", border: "1px solid #dce3dc", borderRadius: 8, padding: "0 10px" }}>
                  <Search size={14} style={{ color: "#74857e", marginRight: 6 }} />
                  <input 
                    type="text" 
                    placeholder="Search account or reason..." 
                    value={searchQuery} 
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ border: "none", background: "transparent", padding: "8px 0", width: "100%", fontSize: 11, outline: "none", boxShadow: "none" }}
                  />
                </div>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: "8px 10px", fontSize: 11, border: "1px solid #dce3dc", borderRadius: 8, background: "#fff" }}>
                  <option value="all">All Statuses</option>
                  <option value="needs_decision">Needs Decision</option>
                  <option value="needs_evidence">Needs Evidence</option>
                  <option value="accepted">Accepted</option>
                  <option value="adjusted">Adjusted</option>
                   <option value="no_future_impact">No Future Impact</option>
                </select>
                <select aria-label="Filter by forecast method" value={filterMethod} onChange={e => setFilterMethod(e.target.value)} style={{ padding: "8px 10px", fontSize: 11, border: "1px solid #dce3dc", borderRadius: 8, background: "#fff" }}>
                  <option value="all">Method: All</option>
                  {METHOD_ORDER.map((method) => <option key={method} value={method}>{METHOD_LABELS[method]}</option>)}
                  <option value="manual_override">Manual Override</option>
                </select>
                <select value={sortBy} onChange={e => setSortBy(e.target.value as "status"|"account")} style={{ padding: "8px 10px", fontSize: 11, border: "1px solid #dce3dc", borderRadius: 8, background: "#fff" }}>
                  <option value="status">Sort: Action Needed</option>
                  <option value="account">Sort: GL Account</option>
                </select>
              </div>
            </div>
            <div className="table-wrap" style={{ margin: 0, borderTop: "1px solid #eef1ed", flex: 1, overflow: "auto" }}>
              <table className="tl-fy-table">
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Account / Spend</th>
                    <th>Explain Result</th>
                    <th>Forecast Method</th>
                    <th>Method Basis</th>
                    <th>Phasing Method</th>
                    <th>System FC</th>
                    <th>Adjustment</th>
                    <th>Current FC</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDecisions.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: "center", padding: "40px 20px", color: "#6a7b74", fontSize: 11 }}>No decisions match the current filters.</td></tr>
                  )}
                  {filteredDecisions.map(decision => (
                    <tr
                      key={decision.decisionId}
                      id={`row-${decision.decisionId}`}
                      className={selectedDecisionId === decision.decisionId ? "current" : ""}
                       onClick={() => { if (!isAuthoritative) setSelectedDecisionId(decision.decisionId); }}
                       style={{ cursor: isAuthoritative ? "default" : "pointer" }}
                    >
                      <td><b>{decision.glAccount}</b></td>
                      <td className="mini" style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {decision.explanation.reason || "Unexplained"}
                        {decision.explanation.nature ? ` · ${decision.explanation.nature}` : ""}
                      </td>
                      <td><button type="button" className="forecast-method-why" onClick={(event) => { event.stopPropagation(); setMethodDetailDecisionId(decision.decisionId); }}><Tag>{METHOD_LABELS[decision.method] || decision.method}</Tag><span><HelpCircle size={11}/> Why?</span></button></td>
                      <td className="mini forecast-basis-cell">{decision.methodReason}</td>
                      <td className="mini">{phasingForMethod(decision.method).method}</td>
                      <td>{formatMoney(decision.systemForecast)}</td>
                      <td className={decision.manualAdjustment !== 0 || decision.treatmentAdjustment !== 0 ? "bad-text" : ""}>
                        {decision.manualAdjustment + decision.treatmentAdjustment !== 0 ? formatMoney(decision.manualAdjustment + decision.treatmentAdjustment) : "—"}
                      </td>
                      <td><b>{formatMoney(decision.currentForecast)}</b></td>
                      <td><Tag tone={decision.status === "needs_decision" || decision.status === "needs_evidence" ? "bad" : "good"}>{STATUS_LABELS[decision.status] || decision.status}</Tag></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <aside className="sticky-panel">
             {isAuthoritative ? (
               <div className="card" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, height: 300, color: "#587069", fontSize: 11, textAlign: "center" }}>
                 <LockKeyhole size={22} />
                 <b>Authoritative Forecast</b>
                 <span>This completed run is immutable. Create a new version from Explain to revise the outlook.</span>
               </div>
             ) : selectedDecision ? (
              <DecisionEditor 
                runId={runId} 
                decision={selectedDecision} 
                 outlook={outlook}
                onClose={() => setSelectedDecisionId(null)}
                onNext={() => {
                   const idx = filteredDecisions.findIndex(d => d.decisionId === selectedDecision.decisionId);
                   if (idx >= 0 && idx < filteredDecisions.length - 1) {
                     setSelectedDecisionId(filteredDecisions[idx + 1].decisionId);
                  } else {
                    setSelectedDecisionId(null);
                  }
                }}
              />
            ) : (
              <div className="card" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, color: "#83918a", fontSize: 11, textAlign: "center" }}>
                Select a forecast decision row to open the treatment editor.
              </div>
            )}
          </aside>
        </div>}

        {workbench === "fy" && <div className="card forecast-progress" style={{ marginTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8f9f7", border: "1px solid #d5dfdb" }}>
          <div>
            <div className="eyebrow">FORECAST PROGRESS</div>
            <div style={{ marginTop: 6, fontSize: 11, color: "#54665e", display: "flex", gap: 12 }}>
              <span><b>{outlook.decisions.length}</b> forecast-relevant lines</span>
              <span><b>{outlook.decisions.filter(d => d.status === "accepted").length}</b> accepted</span>
              <span><b>{outlook.decisions.filter(d => d.status === "adjusted").length}</b> adjusted</span>
              <span><b className={outlook.reviewGate.remainingDecisions > 0 ? "bad-text" : ""}>{outlook.reviewGate.remainingDecisions}</b> remaining</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: 10, color: "#6a7b74", textTransform: "uppercase" }}>Current Forecast</span>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1d3a33" }}>{formatMoney(outlook.currentForecast)}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: 10, color: "#6a7b74", textTransform: "uppercase" }}>vs Budget</span>
              <div style={{ fontSize: 16, fontWeight: 700 }} className={outlook.forecastVsBudget > 0 ? "bad-text" : outlook.forecastVsBudget < 0 ? "good-text" : ""}>
                {Math.abs(outlook.forecastVsBudget) < 0.005 ? "$0" : `${formatMoney(Math.abs(outlook.forecastVsBudget))} ${outlook.forecastVsBudget > 0 ? 'U' : 'F'}`}
              </div>
            </div>
            <div style={{ width: 1, height: 40, background: "#dce5e1" }}></div>
            <button 
              className="tl-btn primary"
              disabled={!canReview || isAuthoritative}
              onClick={handleReviewHandoff}
              style={{ padding: "10px 18px", fontSize: 12 }}
            >
               {isAuthoritative ? "Forecast Authoritative" : <>Continue to Review <ArrowRight size={14} /></>}
            </button>
          </div>
          {!canReview && <div className="forecast-gate-reasons">
            {!outlook.reviewGate.reconciliationPassed && <span>Reconciliation has not passed</span>}
            {!outlook.reviewGate.decisionsPassed && <span>{outlook.reviewGate.remainingDecisions} decisions remain</span>}
            {!outlook.reviewGate.sourceIntegrityPassed && <span>Source integrity checks have not passed</span>}
          </div>}
        </div>}

        <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
          <button className="tl-btn" onClick={() => setDrawer("basis")}>View Forecast Basis</button>
          <button className="tl-btn" onClick={() => setDrawer("methods")}>Forecast Methods</button>
          <button className="tl-btn" onClick={() => setDrawer("audit")}><History size={13} /> View Audit</button>
        </div>

        {drawer && (
          <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 420, background: "#fff", zIndex: 100, boxShadow: "-8px 0 30px rgba(0,0,0,0.08)", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #eef1ec", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fcfcfb" }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>
                {drawer === "audit" ? "Override Audit" : drawer === "methods" ? "Forecast Methods" : "Forecast Basis"}
              </h3>
              <button onClick={() => setDrawer(null)} style={{ border: 0, background: "transparent", color: "#8a9a94", cursor: "pointer", padding: 4 }}><X size={18} /></button>
            </div>
            <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
              {drawer === "audit" && <AuditPanel audit={audit} isLoading={isAuditLoading} />}
              {drawer === "methods" && (
                <div style={{ fontSize: 12, lineHeight: 1.6, color: "#4f645a", display: "flex", flexDirection: "column", gap: 20 }}>
                  <div>
                    <h4 style={{ color: "#234139", margin: "0 0 4px", fontSize: 13 }}>Insufficient Evidence</h4>
                    <p style={{ margin: 0 }}>Flags an open period when no authoritative Budget, commitment, approved plan, or representative Actual history can support a forecast.</p>
                  </div>
                  <div>
                    <h4 style={{ color: "#234139", margin: "0 0 4px", fontSize: 13 }}>Remaining Budget</h4>
                    <p style={{ margin: 0 }}>Assumes the published budget baseline for the remainder of the year. Selected when no stronger future commitment or driver exists.</p>
                  </div>
                  <div>
                    <h4 style={{ color: "#234139", margin: "0 0 4px", fontSize: 13 }}>Run Rate</h4>
                    <p style={{ margin: 0 }}>Extrapolates recent operating history. Automatically selected when reliable certified Actual history exists and no conflicting plan takes precedence.</p>
                  </div>
                  <div>
                    <h4 style={{ color: "#234139", margin: "0 0 4px", fontSize: 13 }}>Commitment / Contract Schedule</h4>
                    <p style={{ margin: 0 }}>Uses authoritative future commitments (contracts, POs, renewals, schedules). This source always overrides historical run rates.</p>
                  </div>
                  <div>
                    <h4 style={{ color: "#234139", margin: "0 0 4px", fontSize: 13 }}>Planned / Driver-Based</h4>
                    <p style={{ margin: 0 }}>Uses explicit approved future planned spend models. Takes precedence over run rate but sits below contractual commitments.</p>
                  </div>
                  <div>
                    <h4 style={{ color: "#234139", margin: "0 0 4px", fontSize: 13 }}>Manual Adjustment</h4>
                    <p style={{ margin: 0 }}>Approved management override applied on top of the deterministic System Forecast. Requires a reason and leaves an immutable audit trail.</p>
                  </div>
                </div>
              )}
              {drawer === "basis" && (
                <div style={{ fontSize: 12, lineHeight: 1.6, color: "#4f645a" }}>
                  <p>The Forecast Basis guarantees that the Current Forecast reconciles exactly to the sum of known exposures, verified plans, modelling estimates, and manual overrides.</p>
                  <p>If TekLedger has no authoritative source data for optional components (e.g. Accruals or Commitments), it explicitly declares "No source data" rather than displaying a false $0.</p>
                  <div className="source-lineage"><span>Run version</span><code>{outlook.runVersion}</code><span>Run ID</span><code>{outlook.runId}</code></div>
                </div>
              )}
            </div>
          </div>
        )}
        {methodDetailDecision && <MethodBasisDrawer decision={methodDetailDecision} outlook={outlook} onClose={() => setMethodDetailDecisionId(null)} />}
      </section>
    </Shell>
  );
}

function DecisionEditor({ runId, decision, outlook, onClose, onNext }: { runId: string, decision: ForecastDecision, outlook: ForecastOutlook, onClose: () => void, onNext: () => void }) {
  const queryClient = useQueryClient();
  const updateMutation = useUpdateForecastDecision();
  
  const [treatment, setTreatment] = useState<ForecastDecisionTreatment>(
    decision.treatment || "no_future_impact"
  );
  const [method, setMethod] = useState<ForecastDecisionMethod>(decision.method);
  const [adjustment, setAdjustment] = useState(decision.manualAdjustment.toString());
  const [reason, setReason] = useState("");
  
  // Parameter states
  const [targetPeriod, setTargetPeriod] = useState<string>("");
  const [sourcePeriod, setSourcePeriod] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [monthlyAdjustment, setMonthlyAdjustment] = useState<string>("");
  const [effectivePeriod, setEffectivePeriod] = useState<string>("");
  const [throughPeriod, setThroughPeriod] = useState<string>("");
  const [totalAmount, setTotalAmount] = useState<string>("");
  const [manualPeriod, setManualPeriod] = useState<string>("");
  const [remainingAllocations, setRemainingAllocations] = useState<Record<number, string>>({});
  const futurePeriods = outlook.monthlyOutlook.filter((item) => item.status === "forecast").map((item) => item.period);
  const accountLines = outlook.lines.filter((line) => line.glAccount === decision.glAccount);
  const sourceAmount = (type: string) => accountLines.flatMap((line) => line.sources).filter((source) => source.type === type).reduce((sum, source) => sum + source.amount, 0);
  const accountAvailability = (kind: "accruals"|"commitments"|"plans", type: string): ForecastAvailability => {
    const global = outlook.sourceAvailability[kind];
    if (global.state === "no_source_data") return global;
    const matching = accountLines.flatMap((line) => line.sources).filter((source) => source.type === type);
    if (!matching.length) return { state: "no_records", amount: null };
    const value = matching.reduce((sum, source) => sum + source.amount, 0);
    return { state: value === 0 ? "verified_zero" : "amount", amount: value };
  };
  const suggestedTreatment: ForecastDecisionTreatment =
    decision.explanation.expectedToReverse ? "timing_shift"
      : String(decision.explanation.nature || "").toLowerCase().includes("structural") ? "run_rate_change"
      : "no_future_impact";
  const phasing = phasingForMethod(decision.method);

  // Sync state when line changes
  useEffect(() => {
    setTreatment(decision.treatment || "no_future_impact");
    setMethod(decision.method);
    setAdjustment(decision.manualAdjustment.toString());
    setReason("");
    setTargetPeriod("");
    setSourcePeriod("");
    setAmount("");
    setMonthlyAdjustment("");
    setEffectivePeriod("");
    setThroughPeriod("");
    setTotalAmount("");
    setManualPeriod("");
    setRemainingAllocations({});
  }, [decision]);

  const finishMutation = (isNext: boolean) => ({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getGetForecastOutlookQueryKey(runId) });
      queryClient.invalidateQueries({ queryKey: getGetForecastAuditQueryKey(runId) });
      if (isNext) onNext();
    }
  });

  const handleDisposition = (status: "accepted"|"needs_evidence", isNext = true) => {
    updateMutation.mutate({ runId, glAccount: decision.glAccount, data: { actor: "ER", status } }, finishMutation(isNext));
  };

  const handleSave = (isNext = false) => {
    const status: ForecastDecisionUpdateStatus = treatment === "no_future_impact" ? "no_future_impact" : "adjusted";
    const nextAdjustment = Number(adjustment) || 0;
    const adjustmentChanged = nextAdjustment !== decision.manualAdjustment;
    const allocations = futurePeriods.flatMap((period) => {
      const value = remainingAllocations[period];
      return value === undefined || value === "" ? [] : [{ period, amount: Number(value) }];
    });
    const parameters =
      treatment === "timing_shift" ? { treatment, amount: Number(amount), sourcePeriod: Number(sourcePeriod), targetPeriod: Number(targetPeriod) }
      : treatment === "one_time_future_adjustment" ? { treatment, amount: Number(amount), targetPeriod: Number(targetPeriod) }
      : treatment === "run_rate_change" ? { treatment, monthlyAdjustment: Number(monthlyAdjustment), effectivePeriod: Number(effectivePeriod), throughPeriod: Number(throughPeriod) }
      : treatment === "remaining_fy_adjustment" ? { treatment, totalAmount: Number(totalAmount), allocations }
      : { treatment: "no_future_impact" as const };
    updateMutation.mutate({
      runId,
      glAccount: decision.glAccount,
      data: {
        actor: "ER",
        status,
         method: method !== decision.method && method !== "mixed" && method !== "insufficient_evidence" ? method as any : undefined,
         manualAdjustment: adjustmentChanged ? nextAdjustment : undefined,
         manualAllocations: adjustmentChanged && nextAdjustment !== 0 ? [{ period: Number(manualPeriod), amount: nextAdjustment }] : adjustmentChanged ? [] : undefined,
         overrideReason: adjustmentChanged && nextAdjustment !== 0 ? reason : undefined,
         parameters,
      }
     }, finishMutation(isNext));
  };
  const treatmentImpact = treatment === "timing_shift" || treatment === "no_future_impact" ? 0
    : treatment === "one_time_future_adjustment" ? Number(amount || 0)
    : treatment === "run_rate_change" && effectivePeriod && throughPeriod
      ? Number(monthlyAdjustment || 0) * Math.max(0, Number(throughPeriod) - Number(effectivePeriod) + 1)
      : treatment === "remaining_fy_adjustment" ? Number(totalAmount || 0) : 0;
  const manualChanged = Number(adjustment || 0) !== decision.manualAdjustment;
  const manualInvalid = manualChanged && Number(adjustment || 0) !== 0 && (!reason.trim() || !manualPeriod);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if (updateMutation.isPending) return;
      if (event.altKey && event.key.toLowerCase() === "a") {
        event.preventDefault();
        handleDisposition("accepted");
      } else if (event.altKey && event.key.toLowerCase() === "e") {
        event.preventDefault();
        handleDisposition("needs_evidence", false);
      } else if ((event.ctrlKey || event.metaKey) && event.key === "Enter" && !manualInvalid) {
        event.preventDefault();
        handleSave(true);
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [decision.decisionId, treatment, method, adjustment, reason, targetPeriod, sourcePeriod, amount, monthlyAdjustment, effectivePeriod, throughPeriod, totalAmount, manualPeriod, remainingAllocations, manualInvalid, updateMutation.isPending]);

  return (
    <div className="card" style={{ padding: 0, display: "flex", flexDirection: "column" }}>
      <div className="card-head" style={{ padding: "16px 18px", margin: 0, borderBottom: "1px solid #eef1ed", background: "#fcfbf9" }}>
        <div>
          <div className="eyebrow">FORECAST TREATMENT</div>
          <h3 style={{ margin: "4px 0 2px" }}>{decision.glAccount}</h3>
          <span className="mini" style={{ color: "#74857e" }}>Department {decision.departmentCode}</span>
        </div>
        <button className="tl-btn" onClick={onClose} style={{ padding: "4px 8px", fontSize: 10 }}>Close</button>
      </div>

      <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <div className="eyebrow" style={{ color: "#74867d", marginBottom: 8 }}>CURRENT FORECAST BASIS</div>
          <div className="forecast-current-method">
            <div><span>Forecast Method</span><b>{METHOD_LABELS[decision.method] || decision.method}</b></div>
            <p>{decision.methodReason}</p>
            <div><span>Phasing Method</span><b>{phasing.method}</b></div>
            <p>{phasing.basis}</p>
          </div>
          <div className="forecast-editor-basis">
            <div><span>YTD Actual</span><b>{formatMoney(sourceAmount("certified_actual"))}</b></div>
            <div><span>Open Accrual</span><b><AvailabilityValue value={accountAvailability("accruals","open_accrual_exposure")} /></b></div>
            <div><span>Future Commitment</span><b><AvailabilityValue value={accountAvailability("commitments","commitment_schedule")} /></b></div>
            <div><span>Planned Spend</span><b><AvailabilityValue value={accountAvailability("plans","planned_driver")} /></b></div>
          </div>
        </div>

        <div>
          <div className="eyebrow" style={{ color: "#74867d", marginBottom: 8 }}>EXPLANATION</div>
          <div style={{ fontSize: 11, display: "flex", flexDirection: "column", gap: 6, background: "#f6f8f5", padding: 12, borderRadius: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#74857e" }}>Reason</span><b>{decision.explanation.reason || "—"}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#74857e" }}>Nature</span><b>{decision.explanation.nature || "—"}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                 <span style={{ color: "#74857e" }}>Expected to Reverse</span><b>{decision.explanation.reversalExpectation || (decision.explanation.expectedToReverse ? "Yes" : "No")}</b>
            </div>
            {decision.explanation.expectedToReverse && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#74857e" }}>Reversal Period</span><b>{decision.explanation.reversalPeriod || "—"}</b>
              </div>
            )}
            {decision.explanation.narrative && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid #e8edea", color: "#4f655b", fontStyle: "italic", lineHeight: 1.4 }}>
                "{decision.explanation.narrative}"
              </div>
            )}
          </div>
        </div>

        <div className="forecast-method-basis">
          <div>
            <span className="eyebrow">CURRENT METHOD</span>
            <b>{METHOD_LABELS[decision.method] || decision.method}</b>
          </div>
          <p>{decision.methodReason}</p>
          {decision.methodEvidence.length > 0 && (
            <details>
              <summary>View source evidence</summary>
              {decision.methodEvidence.map((reference) => <code key={reference}>{reference}</code>)}
            </details>
          )}
        </div>

        <div className="forecast-suggestion">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span className="eyebrow" style={{ color: "#4f655b" }}>SUGGESTED TREATMENT</span>
              <b style={{ display: "block", color: "#1e3831", marginTop: 2 }}>{TREATMENT_LABELS[suggestedTreatment]}</b>
            </div>
            {treatment !== suggestedTreatment && (
              <button 
                className="tl-btn" 
                onClick={() => setTreatment(suggestedTreatment)}
                style={{ padding: "4px 8px", fontSize: 10, background: "#fff", borderColor: "#c8d9ce" }}
              >
                Apply
              </button>
            )}
          </div>
          <small style={{ marginTop: 6, color: "#6a8277" }}>Suggestion only. Confirming a treatment never changes certified Actuals or the System Forecast.</small>
        </div>

        <div>
          <div className="eyebrow" style={{ color: "#74867d", marginBottom: 8 }}>USER DECISION</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
              Treatment
               <select value={treatment || ""} onChange={e => setTreatment(e.target.value as ForecastDecisionTreatment)}>
                {Object.entries(TREATMENT_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </label>

            {treatment === "timing_shift" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
                  Amount
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" />
                </label>
                <div className="mini" style={{ alignSelf: "end", paddingBottom: 8 }}>FY impact: $0</div>
                <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
                  Source Period
                  <select value={sourcePeriod} onChange={e => setSourcePeriod(e.target.value)}><option value="">Select</option>{futurePeriods.map((item) => <option key={item} value={item}>{formatPeriodName(item, outlook.fiscalYear)}</option>)}</select>
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
                  Target Period
                  <select value={targetPeriod} onChange={e => setTargetPeriod(e.target.value)}><option value="">Select</option>{futurePeriods.map((item) => <option key={item} value={item}>{formatPeriodName(item, outlook.fiscalYear)}</option>)}</select>
                </label>
              </div>
            )}

            {treatment === "run_rate_change" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c", gridColumn: "1 / -1" }}>
                  Monthly Adjustment
                  <input type="number" value={monthlyAdjustment} onChange={e => setMonthlyAdjustment(e.target.value)} placeholder="0" />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
                  Effective Period
                  <input type="number" value={effectivePeriod} onChange={e => setEffectivePeriod(e.target.value)} placeholder="1-12" min="1" max="12" />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
                  Through Period
                  <input type="number" value={throughPeriod} onChange={e => setThroughPeriod(e.target.value)} placeholder="1-12" min="1" max="12" />
                </label>
              </div>
            )}

            {treatment === "one_time_future_adjustment" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
                  Amount
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
                  Target Period
                  <input type="number" value={targetPeriod} onChange={e => setTargetPeriod(e.target.value)} placeholder="1-12" min="1" max="12" />
                </label>
              </div>
            )}

            {treatment === "remaining_fy_adjustment" && (
              <div className="forecast-allocation-grid">
                <label style={{ gridColumn: "1 / -1" }}>Total Remaining FY Adjustment<input type="number" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} placeholder="0" /></label>
                {futurePeriods.map((item) => <label key={item}>{formatPeriodName(item, outlook.fiscalYear)}<input type="number" value={remainingAllocations[item] || ""} onChange={(e) => setRemainingAllocations((current) => ({...current,[item]:e.target.value}))} placeholder="0" /></label>)}
                <span className="mini" style={{gridColumn:"1 / -1"}}>Monthly allocation must equal the total. TekLedger will not invent a spread.</span>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#45655c" }}>Change Forecast Method</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {decision.availableMethods.map((m) => (
                  <button
                    key={m}
                    onClick={() => setMethod(m)}
                    className={`tl-btn ${method === m ? 'primary' : ''}`}
                    style={{ padding: '6px 10px', fontSize: 10 }}
                  >
                    {METHOD_LABELS[m] || m}
                  </button>
                ))}
                {decision.method === "mixed" && method === "mixed" && (
                  <button className="tl-btn primary" style={{ padding: '6px 10px', fontSize: 10 }}>Mixed Methods</button>
                )}
              </div>
              <span className="mini">Only methods supported across every open period are available. Manual adjustment remains separate.</span>
            </div>

            <div style={{ padding: "14px 16px", background: "#fcfbf7", borderRadius: 8, border: "1px solid #e8ede9", display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                <span style={{ color: "#74857e" }}>System Forecast</span>
                <b style={{ color: "#344e45" }}>{formatMoney(decision.systemForecast)}</b>
              </div>
              
              {treatmentImpact !== 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                  <span style={{ color: "#74857e" }}>Treatment Impact</span>
                  <b style={{ color: treatmentImpact > 0 ? "#b15e45" : "#27816d" }}>{treatmentImpact > 0 ? "+" : ""}{formatMoney(treatmentImpact)}</b>
                </div>
              )}
              
              <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, fontWeight: 600, color: "#45655c", marginTop: 4 }}>
                <span>Manual Adjustment</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 10, color: "#83968e", fontWeight: 400 }}>+/-</span>
                  <input 
                    type="number" 
                    value={adjustment} 
                    onChange={e => setAdjustment(e.target.value)} 
                    placeholder="0"
                    style={{ width: 100, padding: "6px 8px", textAlign: "right", borderRadius: 6, border: "1px solid #dce5df" }}
                  />
                </div>
              </label>

              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, borderTop: "1px solid #e9ecea", paddingTop: 10, marginTop: 4 }}>
                <span style={{ color: "#1e3831", fontWeight: 800 }}>New Forecast</span>
                <b style={{ fontSize: 15 }}>{formatMoney(decision.systemForecast + treatmentImpact + Number(adjustment || 0))}</b>
              </div>
            </div>

            {manualChanged && Number(adjustment) !== 0 && (
              <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
                Adjustment Period
                <select value={manualPeriod} onChange={(e)=>setManualPeriod(e.target.value)}><option value="">Select future period</option>{futurePeriods.map((item)=><option key={item} value={item}>{formatPeriodName(item,outlook.fiscalYear)}</option>)}</select>
                <span className="mini">The override is allocated explicitly; no monthly spread is inferred.</span>
              </label>
            )}

            {manualChanged && Number(adjustment) !== 0 && (
              <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: "#45655c" }}>
                Override Reason
                <textarea 
                  value={reason} 
                  onChange={e => setReason(e.target.value)} 
                  placeholder="Required for manual adjustment"
                  rows={2}
                />
              </label>
            )}
          </div>
        </div>

        {updateMutation.isError && (
          <div className="source-page-error" role="alert">
            <AlertCircle size={14}/><div><b>Decision was not saved.</b><span>{updateMutation.error.message}</span></div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button 
            className="tl-btn primary" 
            style={{ flex: 1, padding: "10px", fontSize: 11 }} 
            onClick={() => handleSave(true)}
            disabled={updateMutation.isPending || manualInvalid}
            aria-keyshortcuts="Control+Enter Meta+Enter"
            title="Apply and move to the next filtered decision (Ctrl/⌘ + Enter)"
          >
            {updateMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Apply & Next
          </button>
          <button 
            className="tl-btn"
            style={{ padding: "10px", fontSize: 11 }}
            onClick={() => handleSave(false)}
            disabled={updateMutation.isPending || manualInvalid}
          >
            Save
          </button>
        </div>
        <div className="forecast-disposition-actions">
          <button className="tl-btn" style={{ padding: "8px", flex: 1 }} disabled={updateMutation.isPending} onClick={() => handleDisposition("accepted")} aria-keyshortcuts="Alt+A" title="Accept and move to next (Alt+A)">Accept Forecast</button>
          <button className="tl-btn" style={{ padding: "8px", flex: 1 }} disabled={updateMutation.isPending} onClick={() => handleDisposition("needs_evidence", false)} aria-keyshortcuts="Alt+E" title="Mark as Needs Evidence (Alt+E)">Needs Evidence</button>
        </div>
        <div className="mini" style={{ textAlign: "center", color: "#82908a" }}>↑/↓ navigate · Alt+A accept · Alt+E evidence · Ctrl/⌘+Enter apply</div>
      </div>
    </div>
  );
}

function MethodBasisDrawer({ decision, outlook, onClose }: { decision: ForecastDecision; outlook: ForecastOutlook; onClose: () => void }) {
  const phasing = phasingForMethod(decision.method);
  const accountLines = outlook.lines.filter((line) => line.glAccount === decision.glAccount);
  const certifiedPeriods = accountLines.filter((line) => line.lockedActual && line.sources.some((source) => source.type === "certified_actual")).map((line) => line.period);
  const sourceState = (method: string, sourceClass?: "commitments"|"plans") => {
    if (decision.availableMethods.includes(method as any)) return "Available for every open period";
    if (sourceClass && outlook.sourceAvailability[sourceClass].state === "no_source_data") return "No authoritative source connected";
    return "No supporting records for this account";
  };
  return (
    <div className="forecast-drawer" role="dialog" aria-modal="true" aria-label={`Method basis for account ${decision.glAccount}`}>
      <div className="forecast-drawer-head">
        <div><span className="eyebrow">METHOD BASIS</span><h3>{decision.glAccount}</h3></div>
        <button onClick={onClose} aria-label="Close method basis"><X size={18}/></button>
      </div>
      <div className="forecast-drawer-body">
        <section>
          <span>Selected Forecast Method</span>
          <strong>{METHOD_LABELS[decision.method] || decision.method}</strong>
          <p>{decision.methodReason}</p>
        </section>
        <section>
          <h4>Stronger sources checked</h4>
          <dl>
            <div><dt>Commitment / Contract</dt><dd>{sourceState("commitment_schedule", "commitments")}</dd></div>
            <div><dt>Planned / Driver</dt><dd>{sourceState("planned_driver", "plans")}</dd></div>
            <div><dt>Published Budget</dt><dd>{sourceState("remaining_budget")}</dd></div>
            <div><dt>Run Rate eligibility</dt><dd>{decision.availableMethods.includes("run_rate") ? `${certifiedPeriods.length} certified periods; recurring-history rule passed` : `${certifiedPeriods.length} certified periods; recurring-history rule not passed`}</dd></div>
          </dl>
        </section>
        <section>
          <h4>Phasing</h4>
          <dl>
            <div><dt>Phasing Method</dt><dd>{phasing.method}</dd></div>
            <div><dt>Phasing Basis</dt><dd>{phasing.basis}</dd></div>
          </dl>
        </section>
        <section>
          <h4>Source evidence</h4>
          <div className="forecast-evidence-summary"><span>Certified Actuals</span><b>{certifiedPeriods.length ? certifiedPeriods.map((period) => `P${String(period).padStart(2, "0")}`).join("–") : "None"}</b></div>
          {decision.methodEvidence.length > 0 ? <details><summary>Advanced source details</summary>{decision.methodEvidence.map((reference) => <code key={reference}>{reference}</code>)}</details> : <p>No selected-method source reference exists because the line has insufficient evidence.</p>}
        </section>
      </div>
    </div>
  );
}

function AuditPanel({ audit, isLoading }: { audit?: any, isLoading: boolean }) {
  if (isLoading) {
    return <div style={{ display: "flex", gap: 8, color: "#69807a", fontSize: 11 }}><Loader2 size={14} className="animate-spin" /> Loading audit...</div>;
  }

  const entries = audit?.entries || [];

  return (
    <>
      {entries.length === 0 ? (
        <div style={{ color: "#74857e", fontSize: 11, padding: "40px 0", textAlign: "center" }}>
          No manual adjustments recorded.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {entries.map((entry: any) => (
            <div key={entry.auditId} className="snapshot" style={{ background: "#fcfbf7", border: "1px solid #e0e5df", padding: 12, borderRadius: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <strong style={{ fontSize: 11 }}>{entry.lineId}</strong>
                <span className="mini">
                  {new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date(entry.changedAt))}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#45655c" }}>
                Adj: <s>{formatMoney(entry.priorAdjustment)}</s> → <b className={entry.newAdjustment !== 0 ? "bad-text" : ""}>{formatMoney(entry.newAdjustment)}</b>
              </div>
              <span style={{ fontStyle: "italic", fontSize: 10, color: "#74857e" }}>
                "{entry.reason || "No reason provided"}" · {entry.actor}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
